<template>
  <!-- NPopover wraps the sparkline. When clicked, it displays the rich chart content. -->
  <!-- We use placement="left" or "left-start" so it doesn't cover the table headers on the right -->
  <NPopover trigger="click" placement="left-start" style="padding: 0; border-radius: 8px; overflow: hidden; width: 500px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
    <template #trigger>
      <!-- This is the trigger: the small sparkline SVG -->
      <div style="cursor: pointer;" title="Click to view full chart">
        <svg v-if="hasData" width="40" height="15" viewBox="0 0 40 15">
          <path :d="pathD" fill="none" :stroke="lineColor" stroke-width="1.5" />
        </svg>
        <svg v-else width="40" height="15" viewBox="0 0 40 15">
          <!-- Fallback flat gray line when there is insufficient data -->
          <path d="M0,7.5 L40,7.5" fill="none" stroke="#ccc" stroke-width="1.5" />
        </svg>
      </div>
    </template>
    
    <!-- This is the content inside the popover -->
    <div style="padding: 16px;">
      <!-- Header row containing the title and download button -->
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
        <h4 style="margin: 0; font-size: 16px; font-weight: 500; color: black;">{{ row.product || row.commodity }}</h4>
        <div class="relative inline-block">
          <button
            class="dash-action-btn"
            @click="menuOpen = !menuOpen"
          >
            <img :src="DDICONS.download" alt="" class="dash-action-btn__icon" />
            Export Graphic
          </button>
          <div v-show="menuOpen" class="split-menu" role="menu">
            <button role="menuitem" @click="exportAs('png')">Export as PNG</button>
            <button role="menuitem" @click="exportAs('svg')">Export as SVG</button>
          </div>
        </div>
        <!-- <div class="relative" style="position: relative; display: inline-block;">
          <button class="dash-action-btn dash-action-btn--active" @click="menuOpen = !menuOpen" style="padding: 4px 12px; font-size: 12px;">
            Export Graphic
          </button>
          <div v-show="menuOpen" class="split-menu" role="menu">
            <button role="menuitem" @click="exportAs('png')">Export as PNG</button>
            <button role="menuitem" @click="exportAs('svg')">Export as SVG</button>
          </div>
        </div> -->
      </div>
      
      <!-- Chart.js Canvas Container -->
      <div ref="captureDiv" style="height: 220px; width: 100%; background-color: white;">
        <div style="background: url('https://driqq8crwypb5.cloudfront.net/articles/projectblue-data-dash-image.png') center no-repeat; background-size: 60%; height: 100%; width: 100%;">
          <div style="background-color: rgba(255,255,255,0.8); height: 100%; width: 100%;">
            <DynamicChart :chartData="chartData" :options="chartOptions" chartType="line" height="220px" />
          </div>
        </div>
      </div>
    </div>
  </NPopover>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { NPopover } from 'naive-ui';
import DynamicChart from '@/components/DynamicChart.vue';
import * as htmlToImage from 'html-to-image';
import { DDICONS } from "../../composables/generics";

// The component accepts the entire 'row' object from the Naive UI DataTable
const props = defineProps({
  row: {
    type: Object,
    required: true
  }
});

// Menu state and logic
const menuOpen = ref(false);
const captureDiv = ref(null);

const handleClickOutside = (e) => {
  if (!e.target.closest('.relative')) menuOpen.value = false;
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

const exportAs = (type) => {
  menuOpen.value = false;
  if (type === 'svg') {
    downloadAsSvg();
  } else {
    downloadAsPng();
  }
};

const downloadAsSvg = () => {
  if (!captureDiv.value) return;
  htmlToImage.toSvg(captureDiv.value, { 
    backgroundColor: '#ffffff',
    skipFonts: true,
    fontEmbedCSS: ''
  })
      .then(dataUrl => {
        const link = document.createElement("a");
        link.download = `${props.row.product || props.row.commodity || 'chart'}.svg`;
        link.href = dataUrl;
        link.click();
      })
      .catch(err => console.error("Error exporting SVG:", err));
};

const downloadAsPng = () => {
  if (!captureDiv.value) return;
  htmlToImage.toPng(captureDiv.value, { 
    backgroundColor: '#ffffff',
    skipFonts: true,
    fontEmbedCSS: ''
  })
      .then(dataUrl => {
        const link = document.createElement("a");
        link.download = `${props.row.product || props.row.commodity || 'chart'}.png`;
        link.href = dataUrl;
        link.click();
      })
      .catch(err => console.error("Error exporting PNG:", err));
};

// --- Sparkline Logic ---
// Extract valid (non-null, numeric) data points with their slot index
const validPoints = computed(() => {
  const data = props.row.trendData || [];
  return data
    .map((val, index) => ({ val, index }))
    .filter(item => item.val !== null && item.val !== undefined && !isNaN(item.val));
});

// Checks if we have at least 2 data points to draw a line
const hasData = computed(() => validPoints.value.length >= 2);

// Generates the SVG path 'd' attribute based on the data points
const pathD = computed(() => {
  if (!hasData.value) return '';
  const pts = validPoints.value;
  const totalSlots = (props.row.trendData || []).length;
  const divisor = totalSlots > 1 ? totalSlots - 1 : 1;

  const values = pts.map(p => p.val);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1; // Prevent division by zero if all values are identical

  return pts.map((p, i) => {
    const x = (p.index / divisor) * 40; // Scale X proportionally across the full period window (0 to 40)
    const y = 15 - ((p.val - min) / range) * 15;  // Scale Y from 15 to 0 (SVG Y goes down)
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
});

// Determines the color of the sparkline based on the first and last valid value
const lineColor = computed(() => {
  const pts = validPoints.value;
  if (pts.length < 2) return '#ccc';
  const first = pts[0].val;
  const last = pts[pts.length - 1].val;
  
  if (last > first) return '#28a745'; // Green for upward trend
  if (last < first) return '#dc3545'; // Red for downward trend
  return '#6c757d';                   // Gray for neutral
});

// --- Main Chart Logic ---
// Constructs the data object required by vue-chartjs
const chartData = computed(() => {
  return {
    labels: props.row.trendLabels || [],
    datasets: [
      {
        label: 'Price',
        backgroundColor: '#213CE5', // Deep blue color for data points
        borderColor: '#213CE5',     // Deep blue color for the line
        data: props.row.trendData || [],
        yAxisID: 'yRight',          // Explicitly bind to yRight axis
        tension: 0.1,               // Slight smoothing/curve to the line
        pointRadius: 4,             // Radius of the data points
        pointHoverRadius: 6,        // Radius when hovered
        spanGaps: true              // Connect line across missing (null) weeks
      }
    ]
  };
});

// Configuration options for Chart.js
const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false, // Allows the chart to fill its container's height
  plugins: {
    legend: {
      display: false // Hide the legend since we only have one dataset
    },
    tooltip: {
      callbacks: {
        // Format tooltip values with commas (e.g. "1,200")
        label: function(context) {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          if (context.parsed.y !== null && context.parsed.y !== undefined) {
            label += new Intl.NumberFormat('en-US').format(context.parsed.y);
          }
          return label;
        }
      }
    }
  },
  scales: {
    x: {
      grid: {
        display: false // Hide vertical grid lines for a cleaner look
      },
      ticks: {
        font: {
          size: 9
        },
        color: 'black',
        autoSkip: false,
        maxRotation: 45,
        minRotation: 0
      }
    },
    yLeft: {
      position: 'left',
      ticks: {
        display: false // Hide the 1.0 to 0 ticks
      },
      grid: {
        display: false // Hide horizontal grid lines
      },
      border: {
        display: false // Hide the left axis border line
      },
      title: {
        display: true,
        text: props.row.product || props.row.commodity, // Display the product name
        font: {
          size: 12,
          weight: 'bold'
        },
        color: 'black'
      }
    },
    yRight: {
      position: 'right', // Place the axis on the right
      beginAtZero: false, // Don't force Y-axis to 0, allows better zooming on actual values
      grid: {
        display: false // Hide horizontal grid lines
      },
      title: {
        display: true,
        text: `Price (${props.row.units || 'Nominal US$/t'})`,
        font: {
          size: 12
        },
        color: 'black'
      },
      ticks: {
        // Format Y-axis labels with commas
        callback: function(value) {
          return new Intl.NumberFormat('en-US').format(value);
        },
        font: {
          size: 10
        },
        color: 'black'
      }
    }
  }
}));
</script>

<style scoped lang="scss">
.split-menu {
  position: absolute;
  right: 0;
  margin-top: 6px;
  width: 140px;
  background: white;
  border-radius: 6px;
  border: 1px solid rgba(0,0,0,0.08);
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
  z-index: 1000;
}

.split-menu button {
  width: 100%;
  background: transparent;
  border: 0;
  text-align: left;
  cursor: pointer;
  font-size: 12px !important;
  padding: 8px 12px;
  color: black;
}

.split-menu button:hover {
  background: rgba(37,99,235,0.08);
}

/* Button styles duplicated from PricingServices.vue to ensure consistency */
.dash-action-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  font-size: 12px;
  color: #000;
  cursor: pointer;
  padding: 4px 10px 4px 4px;
  white-space: nowrap;
  transition: color 0.15s, border-color 0.15s;

  &--active {
    color: #2184ff;
    font-weight: 600;
    text-decoration: underline;
  }

  &:hover:not(&--active):not(:disabled) {
    color: #2184ff;
    font-weight: 600;
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.dash-action-btn__icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: block;
  border-radius: 50%;
}
</style>
